import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Divider,
  Alert,
  AlertTitle,
  useTheme,
  LinearProgress,
  Chip
} from '@mui/material';

// Future component imports
// import ContractComplianceChart from '../../components/dashboard/ContractComplianceChart';
// import CycleTimeChart from '../../components/dashboard/CycleTimeChart';
// import PaymentTermsChart from '../../components/dashboard/PaymentTermsChart';
// import ProcessEfficiencyByBUChart from '../../components/dashboard/ProcessEfficiencyByBUChart';

interface BusinessUnitMetrics {
  name: string;
  region: string;
  offContractSpend: number;
  offContractPercentage: number;
  poCycleTime: number; // In days
  invoiceCycleTime: number; // In days
  latePayments: number;
  latePaymentPercentage: number;
  missedDiscounts: number;
  missedDiscountValue: number;
}

const ProcessEfficiencyPage: React.FC = () => {
  const theme = useTheme();

  // Mock data for demonstration
  const [businessUnits, setBusinessUnits] = useState<BusinessUnitMetrics[]>([
    {
      name: 'North America Operations',
      region: 'North America',
      offContractSpend: 3200000,
      offContractPercentage: 12,
      poCycleTime: 3.2,
      invoiceCycleTime: 8.5,
      latePayments: 156,
      latePaymentPercentage: 18,
      missedDiscounts: 43,
      missedDiscountValue: 87000
    },
    {
      name: 'EMEA Central',
      region: 'EMEA',
      offContractSpend: 2800000,
      offContractPercentage: 22,
      poCycleTime: 4.8,
      invoiceCycleTime: 12.3,
      latePayments: 211,
      latePaymentPercentage: 26,
      missedDiscounts: 78,
      missedDiscountValue: 124000
    },
    {
      name: 'APAC Manufacturing',
      region: 'APAC',
      offContractSpend: 1900000,
      offContractPercentage: 18,
      poCycleTime: 3.9,
      invoiceCycleTime: 9.2,
      latePayments: 132,
      latePaymentPercentage: 21,
      missedDiscounts: 52,
      missedDiscountValue: 93000
    },
    {
      name: 'Latin America',
      region: 'LATAM',
      offContractSpend: 1200000,
      offContractPercentage: 31,
      poCycleTime: 5.6,
      invoiceCycleTime: 14.5,
      latePayments: 87,
      latePaymentPercentage: 32,
      missedDiscounts: 41,
      missedDiscountValue: 68000
    },
    {
      name: 'Global R&D',
      region: 'Global',
      offContractSpend: 950000,
      offContractPercentage: 9,
      poCycleTime: 2.8,
      invoiceCycleTime: 7.4,
      latePayments: 43,
      latePaymentPercentage: 14,
      missedDiscounts: 22,
      missedDiscountValue: 35000
    }
  ]);

  // Business Rules Implementation
  const offContractThreshold = 20; // Flag off-contract spend > 20%
  const cycleTimeBenchmarks = {
    po: 4.0, // Industry average PO cycle time in days
    invoice: 10.0 // Industry average invoice cycle time in days
  };
  const latePaymentThreshold = 20; // Flag late payments > 20%

  // Total metrics across all BUs
  const totalOffContractSpend = businessUnits.reduce((sum, bu) => sum + bu.offContractSpend, 0);
  const totalSpend = businessUnits.reduce((sum, bu) => sum + (bu.offContractSpend / (bu.offContractPercentage / 100)), 0);
  const overallOffContractPercentage = (totalOffContractSpend / totalSpend) * 100;
  
  const totalMissedDiscountValue = businessUnits.reduce((sum, bu) => sum + bu.missedDiscountValue, 0);

  // Find BUs that exceed thresholds
  const highOffContractBUs = businessUnits.filter(bu => bu.offContractPercentage > offContractThreshold);
  const slowCycleTimeBUs = businessUnits.filter(
    bu => bu.poCycleTime > cycleTimeBenchmarks.po * 1.1 || bu.invoiceCycleTime > cycleTimeBenchmarks.invoice * 1.1
  );
  const highLatePaymentBUs = businessUnits.filter(bu => bu.latePaymentPercentage > latePaymentThreshold);

  // Alerts based on business rules
  const offContractAlert = overallOffContractPercentage > offContractThreshold && (
    <Alert severity="warning" sx={{ mt: 2 }}>
      <AlertTitle>High Off-Contract Spend</AlertTitle>
      Overall off-contract spend is {overallOffContractPercentage.toFixed(1)}% (${totalOffContractSpend.toLocaleString()}),
      exceeding the threshold of {offContractThreshold}%. The following business units have particularly high rates:
      <ul style={{ marginTop: '8px', marginBottom: '0px' }}>
        {highOffContractBUs.map((bu, index) => (
          <li key={index}>
            {bu.name}: {bu.offContractPercentage}% off-contract (${bu.offContractSpend.toLocaleString()})
          </li>
        ))}
      </ul>
    </Alert>
  );

  const cycleTimeAlert = slowCycleTimeBUs.length > 0 && (
    <Alert severity="info" sx={{ mt: 2 }}>
      <AlertTitle>Cycle Time Analysis</AlertTitle>
      {slowCycleTimeBUs.length} business units have PO or invoice cycle times exceeding benchmarks by more than 10%:
      <ul style={{ marginTop: '8px', marginBottom: '0px' }}>
        {slowCycleTimeBUs.map((bu, index) => (
          <li key={index}>
            {bu.name}: PO cycle {bu.poCycleTime.toFixed(1)} days ({(bu.poCycleTime/cycleTimeBenchmarks.po*100).toFixed(0)}% of benchmark), 
            Invoice cycle {bu.invoiceCycleTime.toFixed(1)} days ({(bu.invoiceCycleTime/cycleTimeBenchmarks.invoice*100).toFixed(0)}% of benchmark)
          </li>
        ))}
      </ul>
    </Alert>
  );

  const latePaymentAlert = highLatePaymentBUs.length > 0 && (
    <Alert severity="error" sx={{ mt: 2 }}>
      <AlertTitle>Late Payments & Missed Discounts</AlertTitle>
      Total value of missed early-payment discounts: ${totalMissedDiscountValue.toLocaleString()}
      <ul style={{ marginTop: '8px', marginBottom: '0px' }}>
        {highLatePaymentBUs.map((bu, index) => (
          <li key={index}>
            {bu.name}: {bu.latePaymentPercentage}% late payments, missed ${bu.missedDiscountValue.toLocaleString()} in discounts
          </li>
        ))}
      </ul>
    </Alert>
  );

  const renderProgressBar = (value: number, threshold: number, label: string) => {
    let color = 'primary';
    if (value > threshold) {
      color = 'error';
    } else if (value > threshold * 0.8) {
      color = 'warning';
    } else {
      color = 'success';
    }

    return (
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        <Typography variant="body2" sx={{ minWidth: 150 }}>{label}:</Typography>
        <Box sx={{ width: '100%', mr: 1 }}>
          <LinearProgress 
            variant="determinate" 
            value={Math.min(value / threshold * 100, 100)} 
            color={color as any}
            sx={{ height: 8, borderRadius: 4 }}
          />
        </Box>
        <Box sx={{ minWidth: 60 }}>
          <Typography variant="body2" color="text.secondary">
            {value.toFixed(1)}%
            <Chip 
              label={threshold + '%'} 
              size="small" 
              variant="outlined"
              sx={{ ml: 1, height: 20, fontSize: '0.7rem' }}
            />
          </Typography>
        </Box>
      </Box>
    );
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Process Efficiency Indicators
      </Typography>

      <Grid container spacing={3}>
        {/* Contract Compliance Chart */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: '350px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography variant="h6" color="text.secondary">
              Contract Compliance by Business Unit
              {/* <ContractComplianceChart /> will be placed here */}
            </Typography>
          </Paper>
        </Grid>

        {/* Cycle Time Chart */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: '350px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography variant="h6" color="text.secondary">
              PO & Invoice Cycle Times
              {/* <CycleTimeChart /> will be placed here */}
            </Typography>
          </Paper>
        </Grid>

        {/* Process KPIs - Summary View */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Process Efficiency Key Metrics
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Box sx={{ p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                    <Typography variant="subtitle1" gutterBottom>
                      Compliance Metrics
                    </Typography>
                    {renderProgressBar(overallOffContractPercentage, offContractThreshold, 'Off-Contract Spend')}
                    {renderProgressBar(
                      businessUnits.reduce((sum, bu) => sum + bu.latePaymentPercentage * bu.latePayments, 0) / 
                      businessUnits.reduce((sum, bu) => sum + bu.latePayments, 0), 
                      latePaymentThreshold, 
                      'Late Payments'
                    )}
                  </Box>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Box sx={{ p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                    <Typography variant="subtitle1" gutterBottom>
                      Cycle Time Metrics (Days)
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <Typography variant="body2" sx={{ minWidth: 150 }}>Avg. PO Cycle Time:</Typography>
                      <Box sx={{ width: '100%', mr: 1 }}>
                        <LinearProgress 
                          variant="determinate" 
                          value={Math.min((businessUnits.reduce((sum, bu) => sum + bu.poCycleTime, 0) / businessUnits.length) / cycleTimeBenchmarks.po * 100, 100)} 
                          color={(businessUnits.reduce((sum, bu) => sum + bu.poCycleTime, 0) / businessUnits.length) > cycleTimeBenchmarks.po ? 'warning' : 'success'}
                          sx={{ height: 8, borderRadius: 4 }}
                        />
                      </Box>
                      <Box sx={{ minWidth: 70 }}>
                        <Typography variant="body2" color="text.secondary">
                          {(businessUnits.reduce((sum, bu) => sum + bu.poCycleTime, 0) / businessUnits.length).toFixed(1)} days
                        </Typography>
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <Typography variant="body2" sx={{ minWidth: 150 }}>Avg. Invoice Cycle:</Typography>
                      <Box sx={{ width: '100%', mr: 1 }}>
                        <LinearProgress 
                          variant="determinate" 
                          value={Math.min((businessUnits.reduce((sum, bu) => sum + bu.invoiceCycleTime, 0) / businessUnits.length) / cycleTimeBenchmarks.invoice * 100, 100)} 
                          color={(businessUnits.reduce((sum, bu) => sum + bu.invoiceCycleTime, 0) / businessUnits.length) > cycleTimeBenchmarks.invoice ? 'warning' : 'success'}
                          sx={{ height: 8, borderRadius: 4 }}
                        />
                      </Box>
                      <Box sx={{ minWidth: 70 }}>
                        <Typography variant="body2" color="text.secondary">
                          {(businessUnits.reduce((sum, bu) => sum + bu.invoiceCycleTime, 0) / businessUnits.length).toFixed(1)} days
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </Grid>
              </Grid>
              
              {/* Insights from Business Rules */}
              {offContractAlert}
              {cycleTimeAlert}
              {latePaymentAlert}
            </CardContent>
          </Card>
        </Grid>

        {/* Payment Terms Chart */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: '350px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography variant="h6" color="text.secondary">
              Payment Terms & Early Payment Discounts
              {/* <PaymentTermsChart /> will be placed here */}
            </Typography>
          </Paper>
        </Grid>

        {/* Business Unit Efficiency Chart */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: '350px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography variant="h6" color="text.secondary">
              Process Efficiency by Business Unit
              {/* <ProcessEfficiencyByBUChart /> will be placed here */}
            </Typography>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ProcessEfficiencyPage; 